
import React, { useEffect } from 'react';
import { Navigation } from '../../components/feature/Navigation';
import { Button } from '../../components/base/Button';
import { Card } from '../../components/base/Card';

export default function VoicePage() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const features = [
    {
      title: "Persistent Memory",
      description: "Every conversation builds upon the last, creating a continuous dialogue that remembers context, preferences, and history.",
      icon: "ri-brain-line"
    },
    {
      title: "Natural Understanding",
      description: "Advanced language processing that comprehends nuance, emotion, and intent beyond simple keyword matching.",
      icon: "ri-chat-voice-line"
    },
    {
      title: "Contextual Awareness",
      description: "Maintains awareness of ongoing projects, relationships, and long-term goals across all interactions.",
      icon: "ri-eye-line"
    },
    {
      title: "Adaptive Learning",
      description: "Continuously improves its understanding of your communication style and preferences over time.",
      icon: "ri-graduation-cap-line"
    }
  ];

  const useCases = [
    {
      title: "Personal Assistant",
      description: "A truly personal AI that knows your schedule, preferences, and goals, helping you manage life more effectively.",
      image: "Professional AI assistant helping with daily tasks, modern office environment, clean minimalist design, soft lighting, technology integration, futuristic workspace"
    },
    {
      title: "Business Intelligence",
      description: "Enterprise-grade conversational AI that maintains institutional memory and provides consistent support across teams.",
      image: "Corporate meeting room with AI hologram display, business professionals discussing data, modern glass conference room, blue holographic projections, professional atmosphere"
    },
    {
      title: "Creative Collaboration",
      description: "An AI partner that remembers your creative projects, artistic vision, and evolving ideas across multiple sessions.",
      image: "Artist studio with AI interface, creative workspace, digital art tools, inspirational lighting, modern creative environment, artistic collaboration between human and AI"
    }
  ];

  return (
    <div className="min-h-screen bg-brand-indigo text-brand-off-white overflow-x-hidden">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-brand-indigo via-indigo-900 to-purple-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 bg-brand-teal/20 rounded-full flex items-center justify-center animate-pulse">
              <i className="ri-mic-line text-4xl text-brand-teal"></i>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              Whence
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-blue-400"> Voice</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Natural language processing with persistent memory and contextual understanding
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                variant="primary" 
                className="bg-brand-teal hover:bg-brand-teal/80 px-8 py-4 text-lg"
              >
                Request Early Access
              </Button>
              <Button 
                variant="secondary" 
                className="border-brand-teal text-brand-teal hover:bg-brand-teal/10 px-8 py-4 text-lg"
              >
                View Documentation
              </Button>
            </div>
            
            <div className="flex items-center justify-center text-brand-teal">
              <div className="w-2 h-2 bg-brand-teal rounded-full mr-2 animate-pulse"></div>
              <span className="text-sm font-medium">In Development • Alpha Release Q2 2024</span>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes It Different */}
      <section className="py-24 bg-gradient-to-b from-black to-brand-indigo/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Beyond Traditional
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-purple-400"> AI</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-brand-teal to-purple-400 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              While other AI systems forget each conversation, Whence Voice remembers everything, creating truly continuous intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Card variant="glass" className="p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Traditional AI</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                    Starts fresh every conversation
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                    No memory of past interactions
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                    Repeats questions and mistakes
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                    Surface-level understanding
                  </li>
                </ul>
              </Card>
            </div>
            
            <div>
              <Card variant="glass" className="p-8 border-2 border-brand-teal/30">
                <h3 className="text-2xl font-bold text-white mb-6">Whence Voice</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start">
                    <i className="ri-check-circle-line text-brand-teal mt-1 mr-3 flex-shrink-0"></i>
                    Builds on every conversation
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-circle-line text-brand-teal mt-1 mr-3 flex-shrink-0"></i>
                    Persistent memory across sessions
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-circle-line text-brand-teal mt-1 mr-3 flex-shrink-0"></i>
                    Learns your preferences over time
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-circle-line text-brand-teal mt-1 mr-3 flex-shrink-0"></i>
                    Deep contextual understanding
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 bg-gradient-to-b from-brand-indigo/20 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Core
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-blue-400"> Features</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-brand-teal to-blue-400 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <Card
                key={feature.title}
                variant="glass"
                hover={true}
                className="p-8 group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-brand-teal/30 transition-all duration-300">
                  <i className={`${feature.icon} text-3xl text-brand-teal`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-brand-teal transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gradient-to-b from-black to-brand-indigo/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Real-World
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-brand-teal"> Applications</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-brand-teal mx-auto rounded-full"></div>
          </div>

          <div className="space-y-16">
            {useCases.map((useCase, index) => (
              <div key={useCase.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <Card variant="glass" className="p-8">
                    <h3 className="text-3xl font-bold text-white mb-6">{useCase.title}</h3>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      {useCase.description}
                    </p>
                  </Card>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative rounded-2xl overflow-hidden">
                    <img 
                      src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28useCase.image%29%7D&width=600&height=400&seq=${index + 10}&orientation=landscape`}
                      alt={useCase.title}
                      className="w-full h-80 object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-indigo/60 to-transparent"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-24 bg-gradient-to-b from-brand-indigo/20 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Technical
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-purple-400"> Overview</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-brand-teal to-purple-400 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card variant="glass" className="p-8 text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-cpu-line text-3xl text-blue-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Advanced NLP</h3>
              <p className="text-gray-300">
                State-of-the-art language models with custom memory architecture
              </p>
            </Card>

            <Card variant="glass" className="p-8 text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-database-2-line text-3xl text-purple-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Memory System</h3>
              <p className="text-gray-300">
                Persistent storage with intelligent retrieval and context management
              </p>
            </Card>

            <Card variant="glass" className="p-8 text-center">
              <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-brand-teal"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Privacy First</h3>
              <p className="text-gray-300">
                End-to-end encryption with user-controlled data retention policies
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-brand-indigo via-purple-900 to-brand-indigo relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Experience
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-purple-400"> True AI?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the waitlist for early access to Whence Voice and be among the first to experience AI with memory.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              className="bg-brand-teal hover:bg-brand-teal/80 px-8 py-4 text-lg"
            >
              Join Waitlist
            </Button>
            <Button 
              variant="secondary" 
              className="border-brand-off-white text-brand-off-white hover:bg-brand-off-white/10 px-8 py-4 text-lg"
              onClick={() => window.history.back()}
            >
              ← Back to Home
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-indigo border-t border-brand-teal/20 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="w-8 h-8 mr-3 relative">
                <div className="absolute inset-0 bg-brand-teal/20 rounded-full blur-sm"></div>
                <img 
                  src="https://static.readdy.ai/image/7c34e572229199a03c653ba249394690/86126c0a2c4a8a5efa2a79660917f6e5.png" 
                  alt="Whence Logo" 
                  className="w-full h-full object-contain relative z-10 drop-shadow-lg filter brightness-110"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(29, 211, 176, 0.3))' }}
                />
              </div>
              <h3 className="text-2xl font-bold text-brand-off-white font-pacifico mr-4">Whence</h3>
              <span className="text-gray-400">Voice • The future of conversation</span>
            </div>
            <div className="text-gray-400 text-sm">
              © 2024 Whence. Shaping the future of intelligence.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
